import { useEffect } from "react";
import { X } from "lucide-react";

/** Simple confirmation modal for destructive actions.
 *
 *  Props:
 *    open, onClose, onConfirm, title, message, confirmLabel, danger (bool)
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "",
  confirmLabel = "Confirm",
  danger = false,
  busy = false,
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-5"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      data-testid="confirm-dialog"
    >
      <div
        className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 relative hbe-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-2 text-hbe-ink/45 hover:text-hbe-primary disabled:opacity-40"
          aria-label="Close"
          data-testid="confirm-close"
        >
          <X size={18} />
        </button>
        <h3 className="font-serif text-xl text-hbe-primary pr-8">{title}</h3>
        {message && (
          <div className="mt-3 text-sm text-hbe-ink/75 whitespace-pre-line">
            {message}
          </div>
        )}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="hbe-btn-ghost disabled:opacity-40"
            data-testid="confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium min-h-[44px] transition disabled:opacity-40 ${
              danger
                ? "bg-red-700 text-white hover:bg-red-800"
                : "bg-hbe-primary text-white hover:bg-[#04203a]"
            }`}
            data-testid="confirm-ok"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
